import { FC, useState, useEffect } from 'react';
import {
  CardRadioButton,
  IconStyle,
  MLPVersion,
  Tag,
  type TagPropsTypes,
  type TagTheme,
  TextLink,
  TextLinkIconPosition,
  TextLinkStyle,
  Theme,
  useTranslation,
} from '@nextgen-web-framework/all';
import { SubcriptionPlanCMSResponseType } from '@commerce-subscription-webcomponents/sdk';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import HtmlContent from '../../HtmlContent/HtmlContent';
import { PlanResponseWithTag, PurchaseTypes } from '../types/propTypes';
import styles from './SopPlanCards.module.scss';
import { SopPlanCardsPropTypes } from './types/propTypes';

const PlansInitialDisplayCount = 2;

const SopPlanCards: FC<SopPlanCardsPropTypes> = ({ selectedOption, selectedPlan, plans, updatePlan }) => {
  const t = useTranslation();
  const [viewAllPlansCta, setViewAllPlansCta] = useState<boolean>(true);
  const getPlanTag = (planTag: SubcriptionPlanCMSResponseType['plan_tag']) => {
    let sopPlanTag: TagPropsTypes | undefined = undefined;
    if (planTag?.length) {
      const [tag] = planTag;
      if (tag.tag_label) {
        sopPlanTag = {
          text: tag?.tag_label,
          icon: {
            iconName: tag?.tag_icon?.faicon as IconName,
          },
          theme: tag?.theme as TagTheme,
        };
      }
    }
    return sopPlanTag;
  };

  const cardHeader = (plan: PlanResponseWithTag, selected: boolean) => {
    const planTag = getPlanTag(plan.planTag);
    return (
      <div className={styles['card-header']}>
        {planTag && <Tag {...planTag} />}
        <div className={selected ? styles['radio-label'] : ''}>{plan.cycle?.description}</div>
      </div>
    );
  };

  const viewAllPlans = () => {
    setViewAllPlansCta(false);
  };

  const isSelectedPlanInview = () => {
    if (viewAllPlansCta) {
      for (const [index, plan] of plans.entries()) {
        if (plan.id === selectedPlan && index < PlansInitialDisplayCount) {
          break;
        } else if (
          plan.id === selectedPlan &&
          index >= PlansInitialDisplayCount &&
          selectedOption === PurchaseTypes.SUBSCRIPTION
        ) {
          setViewAllPlansCta(false);
          break;
        }
      }
    } else {
      setViewAllPlansCta(true);
    }
  };

  useEffect(() => {
    if (plans.length > PlansInitialDisplayCount) {
      isSelectedPlanInview();
    } else {
      setViewAllPlansCta(false);
    }
  }, [plans.length]);

  return (
    <div className={styles['sop-plans']} data-testid="sop-plans-card-testid">
      {plans.slice(0, viewAllPlansCta ? PlansInitialDisplayCount : plans.length).map((plan) => {
        const isSelected = selectedOption === PurchaseTypes.SUBSCRIPTION && plan.id === selectedPlan;
        return (
          <CardRadioButton
            key={plan.id}
            id={`sop-${plan.id}`}
            label={cardHeader(plan, isSelected)}
            className={isSelected ? styles['radio-button'] : ''}
            checked={isSelected}
            onChange={() => {
              updatePlan(plan.id, plan.cycle);
            }}
            theme={isSelected ? Theme.PURPLE : Theme.BLACK}
            mlpVersion={MLPVersion.ONE}
            divider={true}
          >
            {plan.shortDescription && (
              <div className={styles['one-time-description']}>
                <HtmlContent content={plan.shortDescription} />
              </div>
            )}
          </CardRadioButton>
        );
      })}
      {viewAllPlansCta && (
        <div className={styles['text-link']}>
          <TextLink
            id="view-all-plans"
            data-testid="view-all-plans-testid"
            text={t('subscription.subscriptionSelection.viewAllPlans.text')}
            onClick={viewAllPlans}
            rightIcon={{ iconName: 'chevron-down', iconStyle: IconStyle.SOLID }}
            iconPosition={TextLinkIconPosition.RIGHT}
            textLinkStyle={TextLinkStyle.ICON}
          />
        </div>
      )}
    </div>
  );
};

export default SopPlanCards;
